import { slotLabel } from '@/shared/time/venueSlots';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router';

import { ApiError } from '@/shared/api-client/workspace';
import { useAuth } from '@/modules/auth/AuthContext';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { Textarea } from '@/shared/ui/components/textarea';
import { useVenueContext } from './VenueContext';
import { DEFAULT_SLOT_GRANULARITY_MINUTES, venueSlotGranularityMinutes } from './slotGranularity';
import {
  useCapacityOverview,
  useCreateCapacityBlock,
  type CapacityHeat,
  type CapacityOverviewBlock,
  type CapacityOverviewBooking,
  type CapacityOverviewCell,
  type CapacityOverviewRoom,
  type CapacityOverviewMvpVisit,
  type CapacityOverviewStandardVisit,
} from './capacityOverview';

type BlockDraft = {
  roomId: string;
  roomName: string;
  start: string;
  durationMinutes: string;
  blockedSeats: string;
  reason: string;
};

type BlockSlot = {
  roomName: string;
  startsAt: string;
  blocks: CapacityOverviewBlock[];
};

const DEFAULT_BLOCKED_SEATS = '1';

type RoomRowProps = {
  room: CapacityOverviewRoom;
  day: string;
  cells: CapacityOverviewCell[];
  canManageBlocks: boolean;
  hoveredCellKey: string | null;
  roomBlocksByCell: Map<string, CapacityOverviewBlock[]>;
  onHoverChange: (value: string | null) => void;
  onCreateBlock: (startsAt: string) => void;
};

export function CapacityOverviewPage() {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const { venueId } = useVenueContext();
  const { venues } = useAuth();
  const venue = venues.find((entry) => entry.id === venueId);
  const venueTimezone = venue?.timezone ?? 'UTC';
  const venueSlotMinutes = venueSlotGranularityMinutes(venue?.slotGranularityMinutes);
  const scope = { kind: 'venue', venueId } as const;
  const canView = auth.can('venue.capacity_overview.view', scope);
  const canManageBlocks = auth.can('venue.capacity_block.manage', scope);

  const [day, setDay] = useState(() => venueToday(venueTimezone));
  const [hoveredCellKey, setHoveredCellKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<BlockDraft | null>(null);
  const [blockSlot, setBlockSlot] = useState<BlockSlot | null>(null);
  const [poolCell, setPoolCell] = useState<CapacityOverviewCell | null>(null);

  useEffect(() => {
    setDay(venueToday(venueTimezone));
  }, [venueId, venueTimezone]);

  const overview = useCapacityOverview(venueId, day, canView);
  const createBlock = useCreateCapacityBlock(venueId);
  // The server bounds the board's day to the venue's operating hours (ADR 0057): in-hours cells
  // become the grid columns, while any out-of-hours demand it flags surfaces as edge badges rather
  // than columns. A closed weekday carries no in-hours cells and renders a "Closed" banner.
  const capacityTotal = overview.data?.capacityTotal ?? 0;
  const closed = overview.data?.closed ?? false;
  const allCells = useMemo(() => overview.data?.cells ?? [], [overview.data?.cells]);
  const cells = useMemo(() => allCells.filter((cell) => !cell.outOfHours), [allCells]);
  const slotGranularityMinutes = useMemo(
    () => slotGranularityMinutesForCells(cells, venueSlotMinutes),
    [cells, venueSlotMinutes],
  );
  const outOfHoursCells = useMemo(() => allCells.filter((cell) => cell.outOfHours), [allCells]);
  const { leadingOutOfHoursCells, trailingOutOfHoursCells } = useMemo(
    () => splitOutOfHoursCells(cells, outOfHoursCells),
    [cells, outOfHoursCells],
  );
  const rooms = overview.data?.rooms ?? [];
  const displayTimezone = overviewTimezone(venueTimezone, overview.data?.timezone);
  const gridTemplateColumns = `220px repeat(${cells.length}, minmax(84px, 1fr))`;
  const roomBlocksByCell = useMemo(
    () => buildRoomBlockIndex(overview.data?.blocks ?? [], cells),
    [overview.data?.blocks, cells],
  );

  if (!canView) return <Forbidden />;

  function closeDraft() {
    setDraft(null);
  }

  function updateDraft<K extends keyof BlockDraft>(field: K, value: BlockDraft[K]) {
    setDraft((current) => (current ? { ...current, [field]: value } : current));
  }

  function openDraft(room: CapacityOverviewRoom, startsAt: string) {
    setDraft({
      roomId: room.roomId,
      roomName: room.name,
      start: toDateTimeLocal(startsAt),
      durationMinutes: String(slotGranularityMinutes),
      blockedSeats: DEFAULT_BLOCKED_SEATS,
      reason: '',
    });
  }

  function openRoomCell(room: CapacityOverviewRoom, startsAt: string) {
    const blocks = roomBlocksByCell.get(roomBlockCellKey(room.roomId, startsAt)) ?? [];
    if (blocks.length > 0) {
      setBlockSlot({ roomName: room.name, startsAt, blocks });
    } else if (canManageBlocks) {
      openDraft(room, startsAt);
    }
  }

  async function submitBlock() {
    if (!draft) return;
    try {
      await createBlock.mutateAsync({
        roomId: draft.roomId,
        startsAt: `${draft.start}:00`,
        durationMinutes: Number(draft.durationMinutes),
        blockedSeats: Number(draft.blockedSeats),
        reason: draft.reason.trim(),
      });
      toast.success(t('audit.actions.capacity_block.created'));
      closeDraft();
    } catch (error) {
      toast.error(t(capacityOverviewErrorKey(error)));
    }
  }

  const trailingOutOfHoursTitle = closed
    ? t('capacityOverview.outOfHours.closed')
    : t('capacityOverview.outOfHours.trailing');
  const blockDurationStep = String(slotGranularityMinutes);

  return (
    <section className="grid gap-4 p-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="grid gap-1">
          <h1 className="text-2xl font-semibold">{t('capacityOverview.pageTitle')}</h1>
          <p className="text-muted-foreground">
            {t('capacityOverview.description', {
              timezone: displayTimezone,
            })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground rounded-full border px-3 py-1 text-xs font-medium tracking-wide uppercase">
            {displayTimezone}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDay(shiftDay(day, -1))}
          >
            <ChevronLeft aria-hidden="true" />
            <span>{t('capacityOverview.actions.previousDay')}</span>
          </Button>
          <div className="min-w-40 rounded-md border px-3 py-2 text-center text-sm font-medium">
            {formatDay(day)}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDay(shiftDay(day, 1))}
          >
            <span>{t('capacityOverview.actions.nextDay')}</span>
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
      </header>

      {overview.isLoading ? (
        <p className="text-muted-foreground">{t('capacityOverview.loading')}</p>
      ) : overview.isError ? (
        <div className="rounded-lg border border-dashed p-6">
          <p className="font-medium">{t('capacityOverview.errors.loadFailed')}</p>
        </div>
      ) : (
        <>
          {closed || cells.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center">
              <p className="font-medium">{t('capacityOverview.closedBanner')}</p>
              <p className="text-muted-foreground text-sm">{t('capacityOverview.closedHint')}</p>
            </div>
          ) : (
            <>
              {leadingOutOfHoursCells.length > 0 ? (
                <OutOfHoursDemand
                  title={t('capacityOverview.outOfHours.leading')}
                  cells={leadingOutOfHoursCells}
                  onSelect={setPoolCell}
                />
              ) : null}
              {cells.some((cell) => cell.occurrence != null) ? (
                <p className="text-muted-foreground text-xs">{t('repeatedSlots')}</p>
              ) : null}
              <CapacityHeatmap
                cells={cells}
                rooms={rooms}
                day={day}
                capacityTotal={capacityTotal}
                canManageBlocks={canManageBlocks}
                hoveredCellKey={hoveredCellKey}
                roomBlocksByCell={roomBlocksByCell}
                onHoverChange={setHoveredCellKey}
                onCreateBlock={openRoomCell}
                onPoolCellClick={setPoolCell}
                gridTemplateColumns={gridTemplateColumns}
                slotGranularityMinutes={slotGranularityMinutes}
              />
            </>
          )}
          {trailingOutOfHoursCells.length > 0 ? (
            <OutOfHoursDemand
              title={trailingOutOfHoursTitle}
              cells={trailingOutOfHoursCells}
              onSelect={setPoolCell}
            />
          ) : null}
        </>
      )}

      <div className="flex flex-wrap gap-4 text-xs" aria-label="Capacity demand legend">
        <span className="rounded border border-blue-500 px-2 py-1">Booked seats</span>
        <span className="rounded border border-amber-500 bg-amber-100 px-2 py-1">
          Blocked seats
        </span>
        <span className="rounded border border-violet-500 bg-violet-100 px-2 py-1 text-violet-950">
          MVP seats
        </span>
        <span className="rounded border border-teal-500 bg-teal-100 px-2 py-1 text-teal-950">
          Standard seats
        </span>
      </div>

      <Dialog open={draft !== null} onOpenChange={(open) => !open && closeDraft()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('capacityOverview.blockDialog.title')}</DialogTitle>
          </DialogHeader>
          {draft ? (
            <form
              className="grid gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                void submitBlock();
              }}
            >
              <div className="grid gap-2">
                <Label htmlFor="capacity-block-room">
                  {t('capacityOverview.blockDialog.fields.room')}
                </Label>
                <Input id="capacity-block-room" value={draft.roomName} readOnly />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="capacity-block-start">
                  {t('capacityOverview.blockDialog.fields.start')}
                </Label>
                <Input
                  id="capacity-block-start"
                  type="datetime-local"
                  value={draft.start}
                  onChange={(event) => updateDraft('start', event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="capacity-block-duration">
                  {t('capacityOverview.blockDialog.fields.duration')}
                </Label>
                <Input
                  id="capacity-block-duration"
                  type="number"
                  min={blockDurationStep}
                  step={blockDurationStep}
                  value={draft.durationMinutes}
                  onChange={(event) => updateDraft('durationMinutes', event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="capacity-block-seats">
                  {t('capacityOverview.blockDialog.fields.blockedSeats')}
                </Label>
                <Input
                  id="capacity-block-seats"
                  type="number"
                  min="1"
                  value={draft.blockedSeats}
                  onChange={(event) => updateDraft('blockedSeats', event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="capacity-block-reason">
                  {t('capacityOverview.blockDialog.fields.reason')}
                </Label>
                <Textarea
                  id="capacity-block-reason"
                  value={draft.reason}
                  onChange={(event) => updateDraft('reason', event.target.value)}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeDraft}>
                  {t('actions.cancel')}
                </Button>
                <Button type="submit" disabled={createBlock.isPending}>
                  {createBlock.isPending
                    ? t('capacityOverview.blockDialog.form.submitting')
                    : t('capacityOverview.blockDialog.form.create')}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={blockSlot !== null} onOpenChange={(open) => !open && setBlockSlot(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Capacity blocks</DialogTitle>
          </DialogHeader>
          {blockSlot ? (
            <div className="grid gap-3">
              <p className="text-muted-foreground text-sm">
                {blockSlot.roomName} · {formatDay(day)} · {formatTime(blockSlot.startsAt)}
              </p>
              <div className="grid gap-2">
                {blockSlot.blocks.map((block) => (
                  <article key={block.id} className="rounded-lg border p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold">{block.blockedSeats} blocked seats</span>
                      <span className="text-muted-foreground font-mono text-xs">
                        {formatTime(block.startsAt)}–
                        {formatTime(addMinutes(block.startsAt, block.durationMinutes))}
                      </span>
                    </div>
                    <p className="mt-1 text-sm">{block.reason}</p>
                  </article>
                ))}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={poolCell !== null} onOpenChange={(open) => !open && setPoolCell(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Demand in this capacity slot</DialogTitle>
          </DialogHeader>
          {poolCell ? (
            <div className="grid gap-3">
              <p className="text-muted-foreground text-sm">
                {formatDay(day)} · {slotLabel(formatTime(poolCell.startsAt), poolCell.occurrence)} ·{' '}
                {poolCell.bookedSeats} booked seats · {poolCell.blockedSeats} blocked seats ·{' '}
                {poolCell.mvpSeats ?? 0} MVP seats · {poolCell.standardSeats ?? 0} Standard seats
              </p>
              <BookingList bookings={poolCell.bookings ?? []} />
              <MvpVisitList visits={poolCell.mvpVisits ?? []} venueId={venueId} />
              <StandardVisitList visits={poolCell.standardVisits ?? []} venueId={venueId} />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}

type CapacityHeatmapProps = {
  cells: CapacityOverviewCell[];
  rooms: CapacityOverviewRoom[];
  day: string;
  canManageBlocks: boolean;
  roomBlocksByCell: Map<string, CapacityOverviewBlock[]>;
  onCreateBlock: (room: CapacityOverviewRoom, startsAt: string) => void;
  onPoolCellClick: (cell: CapacityOverviewCell) => void;
};

type CapacityHeatmapPropsWithGrid = CapacityHeatmapProps & {
  capacityTotal: number;
  hoveredCellKey: string | null;
  onHoverChange: (value: string | null) => void;
  gridTemplateColumns: string;
  slotGranularityMinutes: number;
};

function CapacityHeatmap({
  cells,
  rooms,
  day,
  capacityTotal,
  canManageBlocks,
  hoveredCellKey,
  roomBlocksByCell,
  onHoverChange,
  onCreateBlock,
  onPoolCellClick,
  gridTemplateColumns,
  slotGranularityMinutes,
}: CapacityHeatmapPropsWithGrid) {
  const { t } = useTranslation('common');

  return (
    <div className="overflow-x-auto rounded-xl border">
      <div className="grid min-w-max" style={{ gridTemplateColumns }}>
        <div className="bg-muted/40 sticky left-0 z-20 border-b border-r px-4 py-3 font-medium">
          {t('capacityOverview.columns.row')}
        </div>
        {cells.map((cell, index) => (
          <div
            key={cell.slotStartsAt ?? cell.startsAt}
            className="bg-muted/40 border-b px-2 py-3 text-center text-xs font-medium"
          >
            {cell.occurrence != null || slotGranularityMinutes === 60 || index % 2 === 0
              ? slotLabel(formatTime(cell.startsAt), cell.occurrence)
              : '·'}
          </div>
        ))}

        <div className="bg-card sticky left-0 z-10 border-b border-r px-4 py-3">
          <div className="font-medium">{t('capacityOverview.poolRow')}</div>
          <div className="text-muted-foreground text-xs">
            {t('capacityOverview.poolHint', { total: capacityTotal })}
          </div>
        </div>
        {cells.map((cell) => (
          <PoolCell
            key={`pool-${cell.slotStartsAt ?? cell.startsAt}`}
            cell={cell}
            compact
            onClick={onPoolCellClick}
          />
        ))}

        {rooms.map((room) => (
          <RoomRow
            key={room.roomId}
            room={room}
            day={day}
            cells={cells}
            canManageBlocks={canManageBlocks}
            hoveredCellKey={hoveredCellKey}
            roomBlocksByCell={roomBlocksByCell}
            onHoverChange={onHoverChange}
            onCreateBlock={(startsAt) => onCreateBlock(room, startsAt)}
          />
        ))}
      </div>
    </div>
  );
}

function PoolCell({
  cell,
  compact = false,
  label,
  className = '',
  onClick,
}: {
  cell: CapacityOverviewCell;
  compact?: boolean;
  label?: string;
  className?: string;
  onClick?: (cell: CapacityOverviewCell) => void;
}) {
  const { t } = useTranslation('common');
  const hasBookingDrillIn = cell.bookings !== undefined;
  const isInteractive =
    (cell.bookedSeats > 0 && hasBookingDrillIn) ||
    ((cell.mvpSeats ?? 0) > 0 && cell.mvpVisits !== undefined) ||
    ((cell.standardSeats ?? 0) > 0 && cell.standardVisits !== undefined);
  const content = (
    <>
      {label && <div className="mb-1 text-xs font-medium opacity-75">{label}</div>}
      <div className={compact ? 'text-lg font-semibold' : 'text-xl font-semibold'}>
        {cell.seatsLeft}
      </div>
      <div className="text-xs">{t(`capacityOverview.heat.${cell.heat}` as never)}</div>
      <div className="mt-1 flex flex-wrap justify-center gap-1 text-[10px]">
        <span>{cell.bookedSeats} booked</span>
        <span>{cell.blockedSeats} blocked</span>
        <span className="rounded bg-violet-100 px-1 text-violet-950">{cell.mvpSeats ?? 0} MVP</span>
        <span className="rounded bg-teal-100 px-1 text-teal-950">
          {cell.standardSeats ?? 0} Standard
        </span>
      </div>
    </>
  );
  if (isInteractive) {
    return (
      <button
        type="button"
        aria-label={`${slotLabel(formatTime(cell.startsAt), cell.occurrence)}: ${cell.seatsLeft} seats left, ${cell.bookedSeats} booked seats, ${cell.blockedSeats} blocked seats, ${cell.mvpSeats ?? 0} MVP seats, ${cell.standardSeats ?? 0} Standard seats`}
        onClick={() => onClick?.(cell)}
        className={`${poolHeatClass(cell.heat)} ${compact ? 'border-b px-2 py-3 text-center' : 'p-3'} ${className} hover:brightness-95 focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none`}
      >
        {content}
      </button>
    );
  }
  return (
    <div
      className={`${poolHeatClass(cell.heat)} ${compact ? 'border-b px-2 py-3 text-center' : 'p-3'} ${className}`}
    >
      {content}
    </div>
  );
}

function BookingList({ bookings }: { bookings: CapacityOverviewBooking[] }) {
  if (bookings.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No booking details are available for this slot.
      </p>
    );
  }
  return (
    <div className="grid gap-2">
      {bookings.map((booking) => (
        <article
          key={booking.bookingId}
          className="flex items-center justify-between gap-3 rounded-lg border p-3"
        >
          <div>
            <div className="font-semibold">{booking.reference}</div>
            <div className="text-muted-foreground text-sm">{booking.guestName}</div>
          </div>
          <div className="font-mono text-sm font-semibold">{booking.seats} seats</div>
        </article>
      ))}
    </div>
  );
}

function CustomerName({
  venueId,
  customerId,
  name,
}: {
  venueId: string;
  customerId: string;
  name: string;
}) {
  const auth = useAuthorization();
  return auth.can('customer.view', { kind: 'venue', venueId }) ? (
    <Link
      className="font-semibold underline"
      to={`/loc/${venueId}/customers?holderId=${customerId}`}
    >
      {name}
    </Link>
  ) : (
    <span className="font-semibold underline">{name}</span>
  );
}

function MvpVisitList({
  visits,
  venueId,
}: {
  visits: CapacityOverviewMvpVisit[];
  venueId: string;
}) {
  return (
    <div className="grid gap-2">
      {visits.map((visit) => (
        <article
          key={visit.visitId}
          className="rounded-lg border border-violet-300 bg-violet-50 p-3 text-violet-950"
        >
          <CustomerName venueId={venueId} customerId={visit.holderId} name={visit.holderName} />
          <p className="text-sm">
            {visit.guests} {visit.guests === 1 ? 'guest' : 'guests'} ·{' '}
            {formatTime(visit.windowStartsAt)}–{formatTime(visit.windowEndsAt)}
          </p>
        </article>
      ))}
    </div>
  );
}

function StandardVisitList({
  visits,
  venueId,
}: {
  visits: CapacityOverviewStandardVisit[];
  venueId: string;
}) {
  return (
    <div className="grid gap-2">
      {visits.map((visit) => (
        <article
          key={visit.visitId}
          className="rounded-lg border border-teal-300 bg-teal-50 p-3 text-teal-950"
        >
          <CustomerName venueId={venueId} customerId={visit.customerId} name={visit.customerName} />
          <p className="text-sm">
            {visit.guests} {visit.guests === 1 ? 'guest' : 'guests'} ·{' '}
            {formatTime(visit.windowStartsAt)}–{formatTime(visit.windowEndsAt)} · {visit.passName} –{' '}
            {visit.variantName}
          </p>
        </article>
      ))}
    </div>
  );
}

function RoomRow({
  room,
  day,
  cells,
  canManageBlocks,
  hoveredCellKey,
  roomBlocksByCell,
  onHoverChange,
  onCreateBlock,
}: RoomRowProps) {
  const { t } = useTranslation('common');

  return (
    <>
      <div className="bg-card sticky left-0 z-10 border-b border-r px-4 py-3">
        <div className="font-medium">{room.name}</div>
        <div className="text-muted-foreground text-xs">
          {t('capacityOverview.roomHint', { count: room.capacity })}
        </div>
      </div>
      {cells.map((cell) => {
        const cellKey = roomBlockCellKey(room.roomId, cell.startsAt);
        const roomCellBlocks = roomBlocksByCell.get(cellKey) ?? [];
        const blockedSeats = sumBlockedSeats(roomCellBlocks);
        const hovered = hoveredCellKey === cellKey;
        return (
          <div
            key={`${room.roomId}:${cell.slotStartsAt ?? cell.startsAt}`}
            role="button"
            tabIndex={0}
            aria-label={roomCellAriaLabel(
              room.name,
              day,
              cell.startsAt,
              cell.seatsLeft,
              blockedSeats,
            )}
            className="group hover:bg-muted/40 focus-visible:ring-ring relative border-b px-2 py-3 text-center outline-none focus-visible:ring-2"
            onClick={() => {
              if (roomCellBlocks.length > 0 || canManageBlocks) onCreateBlock(cell.startsAt);
            }}
            onMouseEnter={() => onHoverChange(cellKey)}
            onMouseLeave={() => onHoverChange(null)}
            onFocus={() => onHoverChange(cellKey)}
            onBlur={() => onHoverChange(null)}
            onKeyDown={(event) => {
              if (roomCellBlocks.length === 0 && !canManageBlocks) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onCreateBlock(cell.startsAt);
              }
            }}
          >
            <span className="sr-only">Empty room slot</span>
            {blockedSeats > 0 ? (
              <div
                className="absolute inset-1 rounded-md border border-amber-500/50 bg-amber-200/40 p-2 text-left text-xs"
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(135deg, rgb(245 158 11 / 0.25) 0 6px, transparent 6px 12px)',
                }}
              >
                <div className="font-semibold">-{blockedSeats}</div>
                {roomCellBlocks.map((block) => (
                  <div key={block.id} className="truncate">
                    {block.reason}
                  </div>
                ))}
              </div>
            ) : null}
            {canManageBlocks && roomCellBlocks.length === 0 && hovered ? (
              <div className="absolute inset-x-1 bottom-1 flex justify-center">
                <Button
                  type="button"
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs"
                  onClick={(event) => {
                    event.stopPropagation();
                    onCreateBlock(cell.startsAt);
                  }}
                >
                  <Plus aria-hidden="true" className="size-3.5" />
                  <span>{t('capacityOverview.actions.addBlock')}</span>
                </Button>
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

function buildRoomBlockIndex(blocks: CapacityOverviewBlock[], cells: CapacityOverviewCell[]) {
  const cellStarts = new Set(cells.map((cell) => cell.startsAt));
  const slotGranularityMinutes = slotGranularityMinutesForCells(
    cells,
    DEFAULT_SLOT_GRANULARITY_MINUTES,
  );
  const byCell = new Map<string, CapacityOverviewBlock[]>();
  for (const block of blocks) {
    const slots = Math.max(1, Math.ceil(block.durationMinutes / slotGranularityMinutes));
    for (let index = 0; index < slots; index += 1) {
      const slot = addMinutes(block.startsAt, index * slotGranularityMinutes);
      if (!cellStarts.has(slot)) continue;
      const key = roomBlockCellKey(block.roomId, slot);
      const current = byCell.get(key) ?? [];
      current.push(block);
      byCell.set(key, current);
    }
  }
  return byCell;
}

function slotGranularityMinutesForCells(
  cells: CapacityOverviewCell[],
  fallbackMinutes: number,
): number {
  if (cells.length < 2) return fallbackMinutes;
  const firstCellStart = new Date(cells[0]!.startsAt).getTime();
  const secondCellStart = new Date(cells[1]!.startsAt).getTime();
  const minutesBetweenCells = Math.round((secondCellStart - firstCellStart) / 60_000);
  return Math.max(DEFAULT_SLOT_GRANULARITY_MINUTES, minutesBetweenCells);
}

function OutOfHoursDemand({
  title,
  cells,
  onSelect,
}: {
  title: string;
  cells: CapacityOverviewCell[];
  onSelect: (cell: CapacityOverviewCell) => void;
}) {
  const { t } = useTranslation('common');
  return (
    <section className="grid gap-2 rounded-xl border border-dashed p-4">
      <h2 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{title}</h2>
      <div className="flex flex-wrap gap-2">
        {cells.map((cell) => (
          <button
            key={`ooh-${cell.startsAt}`}
            type="button"
            onClick={() => onSelect(cell)}
            aria-label={t('capacityOverview.outOfHours.ariaLabel', {
              time: formatTime(cell.startsAt),
              booked: cell.bookedSeats,
              blocked: cell.blockedSeats,
              mvp: cell.mvpSeats ?? 0,
              standard: cell.standardSeats ?? 0,
            })}
            className="focus-visible:ring-ring inline-flex items-center gap-2 rounded-full border border-amber-500/50 bg-amber-100 px-3 py-1 text-xs font-medium text-amber-950 hover:brightness-95 focus-visible:ring-2 focus-visible:outline-none"
          >
            {t('capacityOverview.outOfHours.badge', { time: formatTime(cell.startsAt) })}
          </button>
        ))}
      </div>
    </section>
  );
}

function venueToday(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value ?? '1970';
  const month = parts.find((part) => part.type === 'month')?.value ?? '01';
  const day = parts.find((part) => part.type === 'day')?.value ?? '01';
  return `${year}-${month}-${day}`;
}

function shiftDay(day: string, delta: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
}

function formatDay(day: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${day}T12:00:00Z`));
}

function formatTime(startsAt: string): string {
  return startsAt.slice(11, 16);
}

function toDateTimeLocal(startsAt: string): string {
  return startsAt.slice(0, 16);
}

function addMinutes(startsAt: string, minutes: number): string {
  const date = new Date(`${startsAt}Z`);
  date.setUTCMinutes(date.getUTCMinutes() + minutes);
  return date.toISOString().slice(0, 19);
}

function poolHeatClass(heat: CapacityHeat): string {
  switch (heat) {
    case 'full':
      return 'bg-rose-200 text-rose-900';
    case 'near':
      return 'bg-amber-200 text-amber-950';
    case 'filling':
      return 'bg-lime-200 text-lime-950';
    default:
      return 'bg-emerald-100 text-emerald-950';
  }
}

function overviewTimezone(fallback: string, timezone?: string): string {
  return timezone ?? fallback;
}

function capacityOverviewErrorKey(error: unknown) {
  if (error instanceof ApiError && error.code) {
    return `capacityOverview.errors.${error.code}` as const;
  }
  return 'capacityOverview.errors.generic' as const;
}

function roomBlockCellKey(roomId: string, startsAt: string): string {
  return `${roomId}:${startsAt}`;
}

function sumBlockedSeats(blocks: CapacityOverviewBlock[]): number {
  return blocks.reduce((sum, block) => sum + block.blockedSeats, 0);
}

function roomCellAriaLabel(
  roomName: string,
  day: string,
  startsAt: string,
  seatsLeft: number,
  blockedSeats: number,
): string {
  return `${roomName} ${formatDay(day)} ${formatTime(startsAt)} seats left ${seatsLeft} blocked ${blockedSeats}`;
}

function splitOutOfHoursCells(
  inHoursCells: CapacityOverviewCell[],
  outOfHoursCells: CapacityOverviewCell[],
): {
  leadingOutOfHoursCells: CapacityOverviewCell[];
  trailingOutOfHoursCells: CapacityOverviewCell[];
} {
  const firstInHoursStart = inHoursCells[0]?.startsAt;
  const lastInHoursStart = inHoursCells.at(-1)?.startsAt;
  if (!firstInHoursStart || !lastInHoursStart) {
    return {
      leadingOutOfHoursCells: [],
      trailingOutOfHoursCells: outOfHoursCells,
    };
  }
  return {
    leadingOutOfHoursCells: outOfHoursCells.filter((cell) => cell.startsAt < firstInHoursStart),
    trailingOutOfHoursCells: outOfHoursCells.filter((cell) => cell.startsAt > lastInHoursStart),
  };
}
